import React, { useEffect, useState } from "react";
import { Box, Typography, IconButton, CircularProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

import { Doughnut } from "react-chartjs-2";
import api from "../api/axios";

ChartJS.register(ArcElement, Tooltip, Legend);

export default function RigDonutChart() {
  const [loading, setLoading] = useState(true);

  const [navigation, setNavigation] = useState(null);

  const [summary, setSummary] = useState({
    total_meterage: 0,
    sppl_owned: 0,
    third_party: 0,
  });

  const [chartData, setChartData] = useState([]);

  const [isChartHovered, setIsChartHovered] = useState(false);

  /* ================= FETCH DATA ================= */
  const fetchData = async (month = "") => {
    try {
      setLoading(true);

      const { data } = await api.get("/dashboard/rig-donut-chart", {
        params: month ? { month } : {},
      });

      setNavigation(data.navigation || null);

      setSummary(
        data.summary || {
          total_meterage: 0,
          sppl_owned: 0,
          third_party: 0,
        },
      );

      setChartData(data.chart_data || []);
    } catch (err) {
      console.error("Failed to fetch rig donut chart:", err);

      setNavigation(null);

      setSummary({
        total_meterage: 0,
        sppl_owned: 0,
        third_party: 0,
      });

      setChartData([]);
    } finally {
      setLoading(false);
    }
  };

  /* ================= INITIAL LOAD ================= */
  useEffect(() => {
    fetchData();
  }, []);

  /* ================= FORMAT NUMBER ================= */
  const formatMeterage = (value) =>
    Number(value || 0).toLocaleString(undefined, {
      maximumFractionDigits: 2,
    });

  /* ================= CHART DATA ================= */
  const data = {
    labels: chartData.map((rig) => rig.name),

    datasets: [
      {
        data: chartData.map((rig) => Number(rig.value) || 0),

        backgroundColor: chartData.map((rig) => rig.color || "#607D8B"),

        borderColor: "rgba(255,255,255,0.15)",
        borderWidth: 2,

        hoverOffset: 6,
      },
    ],
  };

  /* ================= CHART OPTIONS ================= */
  const options = {
    responsive: true,

    maintainAspectRatio: false,

    cutout: "65%",

    plugins: {
      legend: {
        position: "bottom",

        labels: {
          color: "#ffffff",
          usePointStyle: true,
          pointStyle: "circle",
          padding: 12,

          font: {
            size: 11,
            weight: "500",
          },
        },
      },

      tooltip: {
        backgroundColor: "#222",
        titleColor: "#fff",
        bodyColor: "#fff",
        padding: 10,

        callbacks: {
          label: (context) => {
            const rig = chartData[context.dataIndex];

            return `${rig.name}: ${formatMeterage(rig.value)} m`;
          },

          afterLabel: (context) => {
            const rig = chartData[context.dataIndex];

            return `Ownership: ${rig.ownership}`;
          },
        },
      },
    },
  };

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,

        mt: 3,

        p: {
          xs: 2,
          sm: 3,
        },

        borderRadius: 3,
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        background: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(255,255,255,0.08)",
        boxSizing: "border-box",
      }}
    >
      {/* ================= HEADER ================= */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        {/* PREVIOUS MONTH */}
        <IconButton
          size="small"
          disabled={!navigation?.prev_month || loading}
          onClick={() => fetchData(navigation.prev_month)}
        >
          <ArrowBackIcon />
        </IconButton>

        {/* TITLE */}
        <Box
          sx={{
            minWidth: 0,
            textAlign: "center",
          }}
        >
          <Typography
            variant="h6"
            fontWeight={600}
            sx={{
              fontSize: {
                xs: "0.95rem",
                sm: "1.1rem",
              },
            }}
          >
            Rig-wise Meterage
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: {
                xs: "0.75rem",
                sm: "0.85rem",
              },
            }}
          >
            {navigation?.display_name || "-"}
          </Typography>
        </Box>

        {/* NEXT MONTH */}
        <IconButton
          size="small"
          disabled={!navigation?.next_month || loading}
          onClick={() => fetchData(navigation.next_month)}
        >
          <ArrowForwardIcon />
        </IconButton>
      </Box>

      {/* ================= CONTENT ================= */}
      {loading ? (
        <Box
          sx={{
            height: 360,

            display: "flex",

            alignItems: "center",

            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : chartData.length === 0 ? (
        <Box
          sx={{
            height: 360,

            display: "flex",

            alignItems: "center",

            justifyContent: "center",
          }}
        >
          <Typography color="text.secondary">
            No meterage data available
          </Typography>
        </Box>
      ) : (
        <>
          {/* ================= OWNERSHIP SUMMARY ================= */}
          <Box
            sx={{
              display: "flex",

              justifyContent: "center",

              alignItems: "center",

              gap: {
                xs: 2,
                sm: 3,
              },

              mb: 1,

              flexWrap: "wrap",
            }}
          >
            {/* SPPL OWNED */}
            <Box
              sx={{
                display: "flex",

                alignItems: "center",

                gap: 0.7,
              }}
            >
              <Box
                sx={{
                  width: 9,

                  height: 9,

                  borderRadius: "50%",

                  backgroundColor: "#2E7D32",
                }}
              />

              <Typography
                sx={{
                  fontSize: "0.78rem",

                  color: "rgba(255,255,255,0.7)",
                }}
              >
                SPPL:
              </Typography>

              <Typography
                sx={{
                  fontSize: "0.8rem",

                  fontWeight: 700,

                  color: "#fff",
                }}
              >
                {formatMeterage(summary.sppl_owned)} m
              </Typography>
            </Box>

            {/* THIRD PARTY */}
            <Box
              sx={{
                display: "flex",

                alignItems: "center",

                gap: 0.7,
              }}
            >
              <Box
                sx={{
                  width: 9,

                  height: 9,

                  borderRadius: "50%",

                  backgroundColor: "#C62828",
                }}
              />

              <Typography
                sx={{
                  fontSize: "0.78rem",

                  color: "rgba(255,255,255,0.7)",
                }}
              >
                Third Party:
              </Typography>

              <Typography
                sx={{
                  fontSize: "0.8rem",

                  fontWeight: 700,

                  color: "#fff",
                }}
              >
                {formatMeterage(summary.third_party)} m
              </Typography>
            </Box>
          </Box>

          {/* ================= DONUT ================= */}
          <Box
            sx={{
              position: "relative",

              height: {
                xs: 300,
                sm: 350,
              },

              width: "100%",

              minWidth: 0,
            }}
          >
            <Doughnut
              data={data}
              options={{
                ...options,
                onHover: (event, elements) => {
                  setIsChartHovered(elements.length > 0);
                },
              }}
            />

            {/* ================= CENTER TOTAL ================= */}
            <Box
              sx={{
                position: "absolute",
                top: "43%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
                pointerEvents: "none",
                width: "40%",
                zIndex: 1,

                // Hide center text while hovering over a donut segment
                visibility: isChartHovered ? "hidden" : "visible",
              }}
            >
              <Typography
                sx={{
                  fontSize: {
                    xs: "1.1rem",
                    sm: "1.3rem",
                  },

                  fontWeight: 700,

                  lineHeight: 1.2,
                }}
              >
                {formatMeterage(summary.total_meterage)}
              </Typography>

              <Typography
                sx={{
                  fontSize: "0.7rem",

                  color: "rgba(255,255,255,0.6)",

                  mt: 0.3,
                }}
              >
                Total Meterage (m)
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}
